/**
 * OwnerShell — section layout for every owner/* screen.
 *
 * Replaces app/owner/_layout.tsx.  Wraps owner screens with:
 *   - Auth guard (return null if not an owner or logging out)
 *   - Suspended-account banner + account-only render when suspended
 *   - NavBanner on the side (desktop-web) or top (mobile)
 *   - ActivationBanner above the screen content
 *
 * Scan is still the conceptual landing page but route selection is now
 * handled by RootNavigator's initialRouteName, so this component only
 * renders chrome.
 */
import React, { ReactNode, useState, useEffect } from 'react';
import { View } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { useResponsive } from '../utils/responsive';
import NavBanner, { NavItem } from '../components/NavBanner';
import SubHeader from '../components/SubHeader';
import SuspendedBanner from '../components/SuspendedBanner';
import ActivationBanner from '../components/ActivationBanner';
import ContentContainer from '../components/ContentContainer';

/** Nav items with the sub-owner permission key that unlocks each. */
const allNavItems: (NavItem & { permKey?: string })[] = [
  { icon: 'stats-chart', label: 'Dashboard', route: 'dashboard', permKey: 'owner_panel.analytics' },
  { icon: 'scan', label: 'Wash', route: 'scan' },
  { icon: 'grid', label: 'Locations', route: 'index', permKey: 'owner_panel.locations' },
  { icon: 'ribbon', label: 'Plans', route: 'subscriptions', permKey: 'owner_panel.subscriptions' },
  { icon: 'car', label: 'Fleet', route: 'fleet', permKey: 'owner_panel.fleet_accounts' },
  { icon: 'hardware-chip', label: 'Hardware', route: 'controllers', permKey: 'owner_panel.locations' },
  { icon: 'people', label: 'Staff', route: 'staff', permKey: 'owner_panel.attendants' },
];

const mainRoutes = [...allNavItems.map((i) => i.route), 'settings'];

export default function OwnerShell({ children }: { children: ReactNode }) {
  const { isAuthenticated, role, isLoading, isSuspended, isLoggingOut } = useAuth();
  const { colors } = useTheme();
  const { isDesktopWeb } = useResponsive();

  // Sub-owners only see nav items they hold at least read on; full owners see all.
  const [subOwnerLevels, setSubOwnerLevels] = useState<Record<string, string> | null | undefined>(undefined);
  useEffect(() => {
    if (!isAuthenticated || role !== 'owner') return;
    api<{ isFullOwner: boolean; levels: Record<string, string> | null }>('/api/me/sub-owner-permissions')
      .then(d => setSubOwnerLevels(d.isFullOwner ? null : (d.levels ?? {})))
      .catch(() => setSubOwnerLevels(null)); // fail open to full nav — API 403s protect the data
  }, [isAuthenticated, role]);

  const navItems = subOwnerLevels == null
    ? allNavItems
    : allNavItems.filter(i => !i.permKey || !!subOwnerLevels[i.permKey]);

  if (!isLoading && (!isAuthenticated || role !== 'owner' || isLoggingOut)) return null;

  if (isSuspended) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background }}>
        {children}
        <SuspendedBanner />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isDesktopWeb ? 'row' : 'column',
        backgroundColor: colors.background,
      }}
    >
      <NavBanner
        items={navItems}
        rolePrefix="owner"
        mainRoutes={mainRoutes}
        accountRoute="settings"
        persistOnSubpages
      />
      <View style={{ flex: 1 }}>
        <ActivationBanner />
        <SubHeader rolePrefix="owner" mainRoutes={mainRoutes} />
        {/* footer=false: the shell wraps the screen's scroll view, so a footer
            here pins to the viewport bottom. Screens render PageFooter at
            their own scroll end (inner ContentContainer / ListFooterComponent). */}
        <ContentContainer role="owner" footer={false}>{children}</ContentContainer>
      </View>
    </View>
  );
}
